import { useState } from 'react'
import { Box, Flex, Grid, Input } from '@chakra-ui/react'
import { useFormColors, FormLabel } from '@minetrace/ui/forms'
import { Modal, toaster, Text, useT } from '@minetrace/ui'
import type { CreateTruckRequest } from '../../api/models/request/transportista'
import { TruckFieldLengths } from '../../lib/fieldLengths'

interface TruckModalProps {
  open: boolean
  onClose: () => void
  onSave: (truck: CreateTruckRequest) => void
  initial?: CreateTruckRequest | null
  /** Placas (normalizadas en mayúsculas) ya en la flota de este transportista (excluye la que se edita). */
  existingPlacas?: string[]
}

const EMPTY: CreateTruckRequest = {
  placa: '',
  marca: '',
  modelo: '',
  anio: undefined,
  capacidadToneladas: undefined,
  numeroConstanciaInscripcion: '',
}

// Espejo de los enums backend (TipoVehiculo / FuelType / GpsServiceType).
const VEHICLE_TYPES = [
  { value: 1, label: 'Camión' },
  { value: 2, label: 'Camioneta' },
  { value: 3, label: 'Tráiler' },
]
const FUEL_TYPES = [
  { value: 1, label: 'Diésel' },
  { value: 2, label: 'Gasolina' },
  { value: 3, label: 'GNV' },
  { value: 4, label: 'GLP' },
  { value: 5, label: 'Eléctrico' },
  { value: 6, label: 'Híbrido' },
]
const GPS_TYPES = [
  { value: 1, label: 'API' },
  { value: 2, label: 'Webhook' },
]

export default function TruckModal({ open, onClose, onSave, initial, existingPlacas = [] }: TruckModalProps) {
  const c = useFormColors()
  const { t } = useT()
  // Estado sembrado al montar; el padre remonta el modal con `key` en cada apertura.
  const [truck, setTruck] = useState<CreateTruckRequest>(() => (initial ? { ...initial } : EMPTY))
  const [gpsEnabled, setGpsEnabled] = useState<boolean>(() => Boolean(initial?.gpsProvider))

  const inputStyles = {
    bg: c.bgInput,
    border: 'none',
    borderRadius: 'xl',
    color: c.text,
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const

  const selectStyles: React.CSSProperties = {
    width: '100%',
    padding: '0.625rem 0.75rem',
    borderRadius: '0.75rem',
    border: 'none',
    fontSize: '0.875rem',
    background: c.bgInput,
    color: c.text,
    outline: 'none',
  }

  const set = (patch: Partial<CreateTruckRequest>) => setTruck((p) => ({ ...p, ...patch }))

  // Placa ya registrada en la flota de este transportista (validación temprana, al tipear).
  const placaDup = truck.placa.trim() !== '' && existingPlacas.includes(truck.placa.trim().toUpperCase())
  const canSave = truck.placa.trim() !== '' && !placaDup

  const handleSave = () => {
    if (!truck.placa.trim()) {
      toaster.error({ description: t('transportistas.nuevo.errors.placaRequired') })
      return
    }
    if (placaDup) {
      toaster.create({ type: 'warning', description: t('transportistas.nuevo.truckExtras.duplicate') })
      return
    }
    const result: CreateTruckRequest = { ...truck, placa: truck.placa.trim() }
    if (!gpsEnabled || !result.gpsProvider?.trim()) {
      result.gpsProvider = undefined
      result.gpsType = undefined
      result.gpsUrl = undefined
      result.gpsAccessToken = undefined
      result.gpsIsActive = undefined
    }
    onSave(result)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? t('transportistas.nuevo.truckExtras.editTitle') : t('transportistas.nuevo.truckExtras.addTitle')}
      size="xl"
      footer={
        <Flex gap={3} justifyContent="flex-end">
          <Box as="button" px={4} py={2} borderRadius="lg" border="1px solid" borderColor={c.border} color={c.text} fontWeight="600" fontSize="sm" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={onClose}>
            {t('common.cancel')}
          </Box>
          <Box as="button" px={4} py={2} borderRadius="lg" bg={c.accent} color="white" fontWeight="600" fontSize="sm" cursor={canSave ? 'pointer' : 'not-allowed'} opacity={canSave ? 1 : 0.5} _hover={{ opacity: canSave ? 0.9 : 0.5 }} onClick={handleSave}>
            {t('common.save')}
          </Box>
        </Flex>
      }
    >
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
        <Box>
          <FormLabel required>{t('transportistas.nuevo.fields.placaRequired')}</FormLabel>
          <Input
            placeholder={t('transportistas.nuevo.fields.placaPlaceholder')}
            value={truck.placa}
            onChange={(e) => set({ placa: e.target.value.toUpperCase().slice(0, TruckFieldLengths.placa) })}
            maxLength={TruckFieldLengths.placa}
            fontFamily="monospace"
            {...inputStyles}
            {...(placaDup ? { ring: '1px', ringColor: '#d49a2a' } : {})}
          />
          {placaDup && <Text fontSize="xs" color="#8a5f0b" mt={1}>{t('transportistas.nuevo.truckExtras.duplicate')}</Text>}
        </Box>
        <Box>
          <FormLabel>{t('transportistas.nuevo.fields.vehicleType')}</FormLabel>
          <select value={truck.vehicleTypeId ?? ''} onChange={(e) => set({ vehicleTypeId: e.target.value ? Number(e.target.value) : undefined })} style={selectStyles}>
            <option value="">{t('transportistas.nuevo.fields.selectPlaceholder')}</option>
            {VEHICLE_TYPES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Box>
        <Box>
          <FormLabel>{t('transportistas.nuevo.fields.marca')}</FormLabel>
          <Input placeholder={t('transportistas.nuevo.fields.marcaPlaceholder')} value={truck.marca ?? ''} onChange={(e) => set({ marca: e.target.value.slice(0, TruckFieldLengths.marca) })} maxLength={TruckFieldLengths.marca} {...inputStyles} />
        </Box>
        <Box>
          <FormLabel>{t('transportistas.nuevo.fields.modelo')}</FormLabel>
          <Input placeholder={t('transportistas.nuevo.fields.modeloPlaceholder')} value={truck.modelo ?? ''} onChange={(e) => set({ modelo: e.target.value.slice(0, TruckFieldLengths.modelo) })} maxLength={TruckFieldLengths.modelo} {...inputStyles} />
        </Box>
        <Box>
          <FormLabel>{t('transportistas.nuevo.fields.anio')}</FormLabel>
          <Input type="number" placeholder={t('transportistas.nuevo.fields.anioPlaceholder')} value={truck.anio ?? ''} onChange={(e) => set({ anio: e.target.value ? parseInt(e.target.value) : undefined })} {...inputStyles} />
        </Box>
        <Box>
          <FormLabel>{t('transportistas.nuevo.fields.fuelType')}</FormLabel>
          <select value={truck.fuelTypeId ?? ''} onChange={(e) => set({ fuelTypeId: e.target.value ? Number(e.target.value) : undefined })} style={selectStyles}>
            <option value="">{t('transportistas.nuevo.fields.selectPlaceholder')}</option>
            {FUEL_TYPES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Box>
        <Box>
          <FormLabel>{t('transportistas.nuevo.fields.performance')}</FormLabel>
          <Input type="number" placeholder={t('transportistas.nuevo.fields.performancePlaceholder')} value={truck.performanceKmPerGal ?? ''} onChange={(e) => set({ performanceKmPerGal: e.target.value ? parseFloat(e.target.value) : undefined })} {...inputStyles} />
        </Box>
        <Box>
          <FormLabel>{t('transportistas.nuevo.fields.capacidad')}</FormLabel>
          <Input type="number" placeholder={t('transportistas.nuevo.fields.capacidadPlaceholder')} value={truck.capacidadToneladas ?? ''} onChange={(e) => set({ capacidadToneladas: e.target.value ? parseFloat(e.target.value) : undefined })} {...inputStyles} />
        </Box>
        <Box gridColumn={{ md: 'span 2' }}>
          <FormLabel>{t('transportistas.nuevo.fields.constancia')}</FormLabel>
          <Input placeholder={t('transportistas.nuevo.fields.constanciaPlaceholder')} value={truck.numeroConstanciaInscripcion ?? ''} onChange={(e) => set({ numeroConstanciaInscripcion: e.target.value.slice(0, TruckFieldLengths.numeroConstanciaInscripcion) })} maxLength={TruckFieldLengths.numeroConstanciaInscripcion} {...inputStyles} />
        </Box>
      </Grid>

      {/* Servicio GPS (opcional) */}
      <Box mt={5} pt={4} borderTop="1px solid" borderColor={c.border}>
        <Flex as="label" alignItems="center" gap={2} cursor="pointer">
          <input type="checkbox" checked={gpsEnabled} onChange={(e) => setGpsEnabled(e.target.checked)} />
          <Text fontSize="sm" fontWeight="600" color={c.text}>{t('transportistas.nuevo.truckExtras.gpsTitle')}</Text>
        </Flex>
        <Text tid="transportistas.nuevo.truckExtras.gpsHint" fontSize="xs" color={c.textMuted} mt={1} />
        {gpsEnabled && (
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} mt={3}>
            <Box>
              <FormLabel>{t('transportistas.nuevo.truckExtras.gpsProvider')}</FormLabel>
              <Input placeholder={t('transportistas.nuevo.truckExtras.gpsProviderPlaceholder')} value={truck.gpsProvider ?? ''} onChange={(e) => set({ gpsProvider: e.target.value })} {...inputStyles} />
            </Box>
            <Box>
              <FormLabel>{t('transportistas.nuevo.truckExtras.gpsType')}</FormLabel>
              <select value={truck.gpsType ?? 1} onChange={(e) => set({ gpsType: Number(e.target.value) })} style={selectStyles}>
                {GPS_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Box>
            <Box gridColumn={{ md: 'span 2' }}>
              <FormLabel>{t('transportistas.nuevo.truckExtras.gpsUrl')}</FormLabel>
              <Input placeholder="https://..." value={truck.gpsUrl ?? ''} onChange={(e) => set({ gpsUrl: e.target.value })} {...inputStyles} />
            </Box>
            <Box gridColumn={{ md: 'span 2' }}>
              <FormLabel>{t('transportistas.nuevo.truckExtras.gpsToken')}</FormLabel>
              <Input type="password" placeholder={t('transportistas.nuevo.truckExtras.gpsTokenPlaceholder')} value={truck.gpsAccessToken ?? ''} onChange={(e) => set({ gpsAccessToken: e.target.value })} autoComplete="new-password" {...inputStyles} />
            </Box>
          </Grid>
        )}
      </Box>
    </Modal>
  )
}
